import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CelebrationIcon from '@mui/icons-material/Celebration'
import API from '../../services/API'
import EmployeeSelector from '../../pages/events/EmployeeSelector'

const MAX_LENGTH = 280

// Send a colleague a public shoutout. Shoutouts appear on wallboards and are
// pulled into the recipient's next appraisal as linked feedback.
const SendShoutoutDialog = ({ open, onClose }) => {
  const [recipient, setRecipient] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const reset = () => {
    setRecipient(null)
    setMessage('')
    setError(null)
    setSent(false)
  }

  const close = () => {
    onClose()
    // Let the dialog close before clearing, so the content doesn't flash
    setTimeout(reset, 300)
  }

  const send = async () => {
    setError(null)
    setSending(true)
    const res = await API.post(
      '/shoutouts',
      { to: recipient?._id ?? recipient, shoutOut: message.trim() },
      { skipGlobalErrorHandler: true },
    )
    setSending(false)
    if (res.status === 200) {
      setSent(true)
      setTimeout(close, 1500)
    } else {
      setError(res.data?.error ?? 'Could not send the shoutout')
    }
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CelebrationIcon color="primary" /> Send a shoutout
      </DialogTitle>
      <DialogContent>
        {sent ? (
          <Alert severity="success">Shoutout sent 🎉</Alert>
        ) : (
          <Stack gap={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Recognise a colleague — shoutouts show on wallboards and are
              linked to their next appraisal.
            </Typography>
            <EmployeeSelector
              valueType="user"
              label="Who deserves it?"
              value={recipient}
              onChange={(event, value) => setRecipient(value)}
              preventSelectingSelf
              noPhoto
              fullWidth
            />
            <TextField
              label="What did they do?"
              multiline
              minRows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value.slice(0, MAX_LENGTH))}
              helperText={`${message.length}/${MAX_LENGTH}`}
              fullWidth
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      {!sent && (
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={send}
            disabled={!recipient || !message.trim() || sending}
          >
            {sending ? 'Sending…' : 'Send shoutout'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default SendShoutoutDialog
